import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store/slices/authSlice';
import { authService } from '../../services/authService';
import { vmsService } from '../../services/vmsService';

import { VendorFilterPanel, VendorFilters } from './components/VendorFilterPanel';
import { ManageVendorTypesModal } from './components/ManageVendorTypesModal';

import { RootState } from '../../store';

const getInvoiceHighlightStyle = (receivedDateStr: string | null, paymentStatus: string | null) => {
  if (paymentStatus === 'paid' || !receivedDateStr) return {};
  const receivedDate = new Date(receivedDateStr);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - receivedDate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays <= 30) {
    return { backgroundColor: '#e6fbf1', color: '#044e39', borderLeft: '4px solid #10b981' };
  } else if (diffDays <= 60) {
    return { backgroundColor: '#fff7ed', color: '#7c2d12', borderLeft: '4px solid #f97316' };
  } else {
    return { backgroundColor: '#fef2f2', color: '#7f1d1d', borderLeft: '4px solid #ef4444' };
  }
};

const VendorList: React.FC = () => {
  const [vendors, setVendors] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(() => {
    const saved = localStorage.getItem('vms_vendor_page');
    return saved ? parseInt(saved, 10) : 1;
  });
  const pageSize = 20;

  const [currentFilters, setCurrentFilters] = useState<VendorFilters>(() => {
    const saved = localStorage.getItem('vms_vendor_filters');
    return saved ? JSON.parse(saved) : {};
  });

  const [showFilters, setShowFilters] = useState(false);
  const [showTypesModal, setShowTypesModal] = useState(false);
  const [vmsTab, setVmsTab] = useState<'vendors' | 'invoices'>('invoices');
  const [globalInvoices, setGlobalInvoices] = useState<any[]>([]);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const isFinanceDept = currentUser?.department?.toLowerCase() === 'finance';

  const fetchVendors = async (filters: VendorFilters = currentFilters, currPage = page) => {
    try {
      const data = await vmsService.getVendors({ ...filters, page: currPage, page_size: pageSize } as any);
      setVendors(data.results || data);
      if (data.count !== undefined) {
        setTotalCount(data.count);
      } else {
        setTotalCount((data.results || data).length);
      }
    } catch (err) {
      console.error('Failed to fetch vendors:', err);
    }
  };

  const fetchGlobalInvoices = async () => {
    try {
      const data = await vmsService.getGlobalInvoices();
      setGlobalInvoices(data);
    } catch (err) {
      console.error('Failed to fetch global invoices:', err);
    }
  };

  React.useEffect(() => {
    localStorage.setItem('vms_vendor_page', String(page));
    localStorage.setItem('vms_vendor_filters', JSON.stringify(currentFilters));
    fetchVendors(currentFilters, page);
  }, [page, currentFilters]);

  React.useEffect(() => {
    if (isFinanceDept && vmsTab === 'invoices') {
      fetchGlobalInvoices();
    }
  }, [currentUser, vmsTab, isFinanceDept]);



  // Logout is now handled in the Profile Page

  return (
    <div className="page-container">
      <div className="page-header" style={{ marginBottom: '1.5rem' }}>
        <div className="header-row">
          <h1 className="page-title" style={{ margin: 0 }}>VERA-Vendor Engagement & Relationship Administration</h1>
          <div className="action-cell" style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
            <button className="secondary-btn sm-btn" onClick={() => navigate('/vms/compare')}>Analytics Dashboard</button>
            {currentUser?.role === 'super_admin' && (
              <button className="secondary-btn sm-btn" onClick={() => navigate('/admin/users')}>User Management</button>
            )}
            <button onClick={() => navigate('/profile')} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--primary)', display: 'flex', alignItems: 'center' }} title="Profile">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="32" height="32">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </button>
          </div>
        </div>
        <p className="page-description" style={{ margin: 0, marginTop: '0.5rem' }}>Secured report for Information Management.</p>
      </div>

      <div className="actions-header">
        <button
          className="secondary-btn"
          onClick={() => setShowFilters(!showFilters)}
          style={{ backgroundColor: showFilters ? 'rgba(71, 49, 146, 0.05)' : 'white' }}
        >
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
          </svg>
          {showFilters ? 'Hide Filters' : 'Filters'}
        </button>
        {/* <button className="secondary-btn" onClick={() => setShowTypesModal(true)}>
          Manage Types
        </button> */}
        <button className="primary-btn" onClick={() => navigate('/vms/vendor/add')}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="18" height="18">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Add New Vendor
        </button>
      </div>

      <ManageVendorTypesModal
        isOpen={showTypesModal}
        onClose={() => setShowTypesModal(false)}
      />



      <VendorFilterPanel
        isOpen={showFilters}
        onClose={() => setShowFilters(false)}
        departments={Array.from(new Set(vendors.map(v => v.department).filter(Boolean)))}
        onApply={(filters) => {
          setCurrentFilters(filters);
          setPage(1);
          setShowFilters(false);
        }}
      />

      {isFinanceDept && (
        <div className="tabs-nav" style={{ display: 'flex', gap: '2rem', borderBottom: '1px solid var(--border)', marginBottom: '1.5rem', paddingLeft: '1rem' }}>
          <button
            onClick={() => setVmsTab('vendors')}
            style={{
              padding: '0.75rem 1rem',
              background: 'none',
              border: 'none',
              borderBottom: vmsTab === 'vendors' ? '2px solid var(--primary)' : '2px solid transparent',
              color: vmsTab === 'vendors' ? 'var(--primary)' : 'var(--text-light)',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Vendors List
          </button>
          <button
            onClick={() => setVmsTab('invoices')}
            style={{
              padding: '0.75rem 1rem',
              background: 'none',
              border: 'none',
              borderBottom: vmsTab === 'invoices' ? '2px solid var(--primary)' : '2px solid transparent',
              color: vmsTab === 'invoices' ? 'var(--primary)' : 'var(--text-light)',
              fontWeight: 600,
              fontSize: '1rem',
              cursor: 'pointer'
            }}
          >
            Invoice Dashboard
          </button>
        </div>
      )}

      {vmsTab === 'invoices' && isFinanceDept ? (
        <div className="list-container">
          <h3 style={{ margin: '0 0 1.5rem 0', fontSize: '1.2rem', color: 'var(--text-dark)', fontWeight: 600 }}>All Newly Uploaded Invoices</h3>
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Vendor</th>
                <th>Generated By</th>
                <th>Department</th>
                <th>Doc Type</th>
                <th>Issued Date</th>
                <th>Due Date</th>
                <th>Received Date</th>
                <th>Invoice Amt</th>
                <th>Pay Date</th>
                <th>Paid Amt</th>
                <th>Due Amt</th>
                <th>Pay Status</th>
                <th>UTR Number</th>
                <th>Document</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {globalInvoices.length === 0 ? (
                <tr>
                  <td colSpan={15} style={{ textAlign: 'center', color: 'var(--text-light)' }}>No invoices found.</td>
                </tr>
              ) : (
                globalInvoices.map((ent, idx) => (
                  <tr
                    key={ent.id}
                    onClick={() => navigate(`/vms/vendor/${ent.read_vendor_id}`)}
                    className="clickable-row"
                    style={getInvoiceHighlightStyle(ent.received_by_accounts_date, ent.payment_status)}
                  >
                    <td><strong>{idx + 1}</strong></td>
                    <td><strong style={{ color: 'var(--primary)' }}>{ent.vendor}</strong></td>
                    <td>{ent.uploaded_by || '-'}</td>
                    <td>{ent.department || '-'}</td>
                    <td>{ent.doc_type}</td>
                    <td>{ent.date_issued || '-'}</td>
                    <td>{ent.due_date || '-'}</td>
                    <td>{ent.received_by_accounts_date || '-'}</td>
                    <td>{ent.invoice_amount ? `₹${ent.invoice_amount}` : '-'}</td>
                    <td>{ent.payment_date || '-'}</td>
                    <td>{ent.amount_paid ? `₹${ent.amount_paid}` : '-'}</td>
                    <td>{ent.doc_type?.toLowerCase().includes('invoice') && ent.invoice_amount ? `₹${Math.max(0, parseFloat(ent.invoice_amount) - (parseFloat(ent.amount_paid) || 0)).toFixed(2)}` : '-'}</td>
                    <td>
                      {ent.payment_status ? (
                        <span className="status-badge" style={{
                          backgroundColor: ent.payment_status === 'paid' ? '#d1fae5' : ent.payment_status === 'partially_paid' ? '#fef3c7' : '#fce7f3',
                          color: ent.payment_status === 'paid' ? '#065f46' : ent.payment_status === 'partially_paid' ? '#92400e' : '#9d174d'
                        }}>
                          {ent.payment_status === 'paid' ? 'Paid' : ent.payment_status === 'partially_paid' ? 'Partial' : 'Unpaid'}
                        </span>
                      ) : '-'}
                    </td>
                    <td>{ent.utr_number || '-'}</td>
                    <td>
                      {ent.current_version > 0 ? (
                        <div className="action-cell">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16" style={{ color: 'var(--primary)' }}>
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ color: 'var(--primary)', fontWeight: 600 }}>v{ent.current_version}</span>
                        </div>
                      ) : (
                        <span className="text-muted">No file</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          try {
                            const res = await vmsService.downloadCurrentVendorDoc(ent.read_vendor_id, ent.id);
                            if (res?.url) window.open(res.url, '_blank');
                          } catch (err) {
                            alert('Failed to download document.');
                          }
                        }}
                        className="secondary-btn sm-btn"
                        disabled={ent.current_version === 0}
                        style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                      >
                        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="14" height="14">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div className="list-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Vendor ID</th>
                  <th>Vendor Name</th>
                  {isFinanceDept && <th>Type</th>}
                  <th>Category</th>
                  <th>Service</th>
                  <th>Contact Email</th>
                  <th>Department</th>
                  <th>State</th>
                  <th>Permanent Address</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map(vendor => (
                  <tr
                    key={vendor.id}
                    onClick={() => navigate(`/vms/vendor/${vendor.id}`)}
                    className="clickable-row"
                  >
                    <td><strong>{vendor.vendor_code || vendor.id}</strong></td>
                    <td>{vendor.vendor_name}</td>
                    {isFinanceDept && (
                      <td>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.8rem',
                          fontWeight: 600,
                          backgroundColor: vendor.partner_type === 'lsp' ? '#dbeafe' : vendor.partner_type === 'lender' ? '#fce7f3' : '#f3f4f6',
                          color: vendor.partner_type === 'lsp' ? '#1e40af' : vendor.partner_type === 'lender' ? '#9d174d' : '#374151',
                          border: '1px solid',
                          borderColor: vendor.partner_type === 'lsp' ? '#bfdbfe' : vendor.partner_type === 'lender' ? '#fbcfe8' : '#e5e7eb'
                        }}>
                          {vendor.partner_type === 'lsp' ? 'LSP' : vendor.partner_type === 'lender' ? 'Lender' : 'Vendor'}
                        </span>
                      </td>
                    )}
                    <td>{vendor.vendor_category === 'one_time' ? 'One-time' : vendor.vendor_category === 'regular' ? 'Regular' : vendor.vendor_category === 'retainer' ? 'Retainer' : 'N/A'}</td>
                    <td>{vendor.nature_of_service || 'N/A'}</td>
                    <td>{vendor.email}</td>
                    <td>{vendor.department || 'N/A'}</td>
                    <td>{vendor.state || 'N/A'}</td>
                    <td>{vendor.permanent_address || 'N/A'}</td>
                    <td>
                      <span className={`status-badge ${vendor.status === 'active' ? 'status-active' : 'status-inactive'}`}>
                        {vendor.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <div className="action-cell">
                        {(currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); navigate(`/vms/vendor/edit/${vendor.id}`, { state: { vendor } }); }}
                            className="secondary-btn sm-btn"
                          >
                            Edit
                          </button>
                        )}
                        {vendor.status === 'active' ? (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            if (window.confirm('Set this vendor to inactive?')) {
                              await vmsService.deleteVendor(vendor.id);
                              fetchVendors();
                            }
                          }} className="danger-btn sm-btn">Deactivate</button>
                        ) : (
                          <button onClick={async (e) => {
                            e.stopPropagation();
                            await vmsService.reactivateVendor(vendor.id);
                            fetchVendors();
                          }} className="secondary-btn sm-btn">Reactivate</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', padding: '1rem', background: 'white', borderRadius: '8px', border: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text-light)', fontSize: '0.9rem' }}>
              Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount} vendors
            </span>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <button
                className="secondary-btn sm-btn"
                style={{ padding: '0.25rem 0.5rem' }}
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                title="Previous Page"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" /></svg>
              </button>

              {Array.from({ length: Math.ceil(totalCount / pageSize) || 1 }, (_, i) => i + 1).map(p => (
                <button
                  key={p}
                  className={page === p ? "primary-btn sm-btn" : "secondary-btn sm-btn"}
                  style={{ padding: '0.25rem 0.6rem', minWidth: '32px' }}
                  onClick={() => setPage(p)}
                >
                  {p}
                </button>
              ))}

              <button
                className="secondary-btn sm-btn"
                style={{ padding: '0.25rem 0.5rem' }}
                disabled={page * pageSize >= totalCount}
                onClick={() => setPage(page + 1)}
                title="Next Page"
              >
                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" width="16" height="16"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VendorList;
